/** @format */

import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { updateProductQuantity } from "../store/reducers/cardReducers";
import { updateOrderProductQuantity } from "../store/reducers/orderReducer";

const QuantityInput = ({
  product,
  customerId,
  stockLimit,
  fonction,
  cardId,
  orderId,
  sellerId,
  onSuccess, // ← callback appelé après mise à jour réussie
}) => {
  const dispatch = useDispatch();
  const debounceTimer = useRef(null);
  const initialQuantity = product.quantity;

  const [localQuantity, setLocalQuantity] = useState(
    initialQuantity.toString(),
  );

  const sendQuantityToServer = async (finalQuantity) => {
    try {
      if (fonction === "card") {
        await dispatch(
          updateProductQuantity({
            cardId: product._id,
            newQuantity: finalQuantity,
            state: "update_cart",
          }),
        ).unwrap();
      } else if (fonction === "order") {
        await dispatch(
          updateOrderProductQuantity({
            cardId,
            orderId,
            sellerId,
            newQuantity: finalQuantity,
            state: "update_cart",
          }),
        ).unwrap();
      }
      onSuccess?.(); // ← re-fetch la commande dans OrderDetails
    } catch (err) {
      console.error("Quantity update failed:", err);
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const triggerDebounceUpdate = (newQuantity) => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      sendQuantityToServer(newQuantity);
    }, 500);
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setLocalQuantity(value);
    const parsed = parseInt(value, 10);
    if (!isNaN(parsed) && parsed >= 1 && parsed <= stockLimit) {
      triggerDebounceUpdate(parsed);
    }
  };

  const handleBlur = () => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    const parsed = parseInt(localQuantity, 10);
    if (!localQuantity) {
      setLocalQuantity(initialQuantity.toString());
      return;
    }
    if (!isNaN(parsed) && parsed >= 1 && parsed <= stockLimit) {
      sendQuantityToServer(parsed);
    } else {
      toast.error(`La quantité doit être entre 1 et ${stockLimit}`);
      setLocalQuantity(initialQuantity.toString());
    }
  };

  const increment = () => {
    const parsed = parseInt(localQuantity, 10) || 0;
    if (parsed < stockLimit) {
      const newQ = parsed + 1;
      setLocalQuantity(newQ.toString());
      triggerDebounceUpdate(newQ);
    } else {
      toast.error("Stock maximum atteint");
    }
  };

  const decrement = () => {
    const parsed = parseInt(localQuantity, 10) || 1;
    if (parsed > 1) {
      const newQ = parsed - 1;
      setLocalQuantity(newQ.toString());
      triggerDebounceUpdate(newQ);
    } else {
      toast.error("Quantité minimum 1");
    }
  };

  useEffect(() => {
    setLocalQuantity(initialQuantity.toString());
  }, [initialQuantity]);

  return (
    <div className='inline-flex items-center bg-gray-100 rounded-xl overflow-hidden border border-gray-200 h-8'>
      <button
        onClick={decrement}
        className='px-2.5 h-full text-gray-600 hover:bg-gray-200 font-bold transition-colors text-base leading-none select-none'
      >
        −
      </button>
      <input
        type='number'
        inputMode='numeric'
        min='1'
        max={stockLimit}
        value={localQuantity}
        onChange={handleChange}
        onBlur={handleBlur}
        onWheel={(e) => e.target.blur()}
        className='w-10 text-center bg-transparent border-none outline-none text-sm font-semibold text-gray-800 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none'
      />
      <button
        onClick={increment}
        className='px-2.5 h-full text-gray-600 hover:bg-gray-200 font-bold transition-colors text-base leading-none select-none'
      >
        +
      </button>
    </div>
  );
};

export default QuantityInput;
